import { PdfScaleControls } from "./PdfScaleControls";
import { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  MoreVertical,
  Copy,
  Lock,
  Layers,
  SlidersHorizontal,
  X,
  LoaderCircle,
  Trash2,
  ArrowUpToLine,
  ArrowDownToLine,
  FilePlus2,
} from "lucide-react";
import type { TLImageShape } from "@tldraw/tlschema";
import type { WorkspaceModel } from "./useWorkspace";
import { changePdfPage, extractPdfPages, pdfSourceBytes } from "./pdf-imports";
import { pdfPreview } from "./pdf-preview";
import { sourceMeters } from "./source-scale";
import { notify } from "./api";

export function PdfControls({
  model,
  shape,
  onProperties,
  contextMenu,
}: {
  model: WorkspaceModel;
  shape: TLImageShape;
  onProperties: () => void;
  contextMenu: { x: number; y: number; nonce: number } | null;
}) {
  const asset =
    shape.props.assetId && model.editor.getAsset(shape.props.assetId);
  const page = Number(asset && asset.meta.pdfPage);
  const count = Number(asset && asset.meta.pdfPages);
  const source = String(asset && asset.meta.documentSource);
  const name = String(asset && asset.meta.documentName);
  const hasAttached =
    model.editor.getSortedChildIdsForParent(shape.id).length > 0;
  const [panel, setPanel] = useState<
    "more" | "extract" | "sheets" | "combined" | "scale" | "pages" | null
  >(null);
  const [contextPoint, setContextPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [jump, setJump] = useState(page);
  const [range, setRange] = useState("all");
  const [busy, setBusy] = useState("");
  const [thumbnails, setThumbnails] = useState<
    Array<{ page: number; url: string }>
  >([]);
  const [thumbnailError, setThumbnailError] = useState("");
  const root = useRef<HTMLElement>(null);
  const pagesButton = useRef<HTMLButtonElement>(null);
  const scaleButton = useRef<HTMLButtonElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);
  const job = useRef<AbortController | null>(null);
  useEffect(() => setJump(page), [page]);
  useEffect(() => {
    if (!contextMenu) return;
    setContextPoint({
      x: Math.max(8, Math.min(window.innerWidth - 248, contextMenu.x)),
      y: Math.max(8, Math.min(window.innerHeight - 330, contextMenu.y)),
    });
    setPanel("more");
  }, [contextMenu]);
  useEffect(() => () => job.current?.abort(), []);
  useEffect(() => {
    if (panel !== "pages") return;
    const controller = new AbortController();
    const urls: string[] = [];
    const nearby = Array.from(
      { length: Math.min(5, count) },
      (_, index) =>
        Math.min(Math.max(1, page - 2), Math.max(1, count - 4)) + index,
    );
    setThumbnails([]);
    setThumbnailError("");
    void (async () => {
      const bytes = await pdfSourceBytes(source, controller.signal);
      for (const number of nearby) {
        controller.signal.throwIfAborted();
        const result = await pdfPreview(bytes, number, controller.signal, 150);
        controller.signal.throwIfAborted();
        const url = URL.createObjectURL(result.blob);
        urls.push(url);
        setThumbnails((current) => [...current, { page: number, url }]);
      }
    })().catch((error) => {
      if (!controller.signal.aborted)
        setThumbnailError((error as Error).message);
    });
    return () => {
      controller.abort();
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [panel, page, count, source]);
  useEffect(() => {
    if (!panel) return;
    root.current
      ?.querySelector<HTMLElement>(
        ".pdf-options input, .pdf-options button, .pdf-options a",
      )
      ?.focus();
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setPanel(null);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [panel]);
  if (!count) return null;
  const run = async (
    fn: (signal: AbortSignal) => Promise<unknown>,
    message: string,
  ) => {
    if (job.current) return;
    const controller = new AbortController();
    job.current = controller;
    setBusy(message);
    try {
      await fn(controller.signal);
    } catch (e) {
      if (!controller.signal.aborted) notify((e as Error).message);
    } finally {
      job.current = null;
      setBusy("");
    }
  };
  const go = (next: number) => {
    if (!Number.isInteger(next) || next < 1 || next > count) return;
    setPanel(null);
    void run(
      (signal) =>
        changePdfPage(
          model.editor,
          model.room.id,
          shape,
          next,
          signal,
          model.room.platform === "cloudflare",
        ),
      "Loading page…",
    );
  };
  const closePanel = () => {
    const previous = panel;
    setPanel(null);
    (previous === "pages"
      ? pagesButton
      : previous === "scale"
        ? scaleButton
        : moreButton
    ).current?.focus();
  };
  const calibration = String(shape.meta.calibration || "");
  const scale = sourceMeters(model.editor, shape) > 0;
  const printedScale = calibration.match(/1:\s*[\d,.]+/);
  const scaleLabel = scale ? printedScale?.[0] || "Scale set" : "Not to scale";
  return (
    <section
      ref={root}
      className="pdf-controls"
      aria-label="PDF tools"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          if (panel) closePanel();
          else {
            model.editor.selectNone();
            model.editor.focus();
          }
        } else event.stopPropagation();
      }}
    >
      <div
        className="pdf-control-row"
        role="toolbar"
        aria-label={`PDF pages — ${name}`}
      >
        <div className="pdf-page-navigation" aria-label="Page navigation">
          <button
            type="button"
            title={
              hasAttached
                ? "Extract another page to preserve annotations"
                : "Previous page"
            }
            aria-label="Previous PDF page"
            disabled={!!busy || hasAttached || page <= 1}
            onClick={() => go(page - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            ref={pagesButton}
            className="pdf-page-picker-trigger"
            type="button"
            title={`Choose page · ${name}`}
            aria-label={`Choose PDF page, ${page} of ${count}`}
            aria-expanded={panel === "pages"}
            disabled={!!busy}
            onClick={() => {
              setContextPoint(null);
              setPanel(panel === "pages" ? null : "pages");
            }}
          >
            {page} / {count} <span aria-hidden="true">⌄</span>
          </button>
          <button
            type="button"
            title={
              hasAttached
                ? "Extract another page to preserve annotations"
                : "Next page"
            }
            aria-label="Next PDF page"
            disabled={!!busy || hasAttached || page >= count}
            onClick={() => go(page + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <span className="pdf-control-divider" />
        <button
          ref={scaleButton}
          className="pdf-scale-trigger"
          title={`Page scale · ${calibration || scaleLabel}`}
          aria-label={`Page scale, ${calibration || scaleLabel}`}
          aria-expanded={panel === "scale"}
          onClick={() => {
            setContextPoint(null);
            setPanel(panel === "scale" ? null : "scale");
          }}
        >
          {scaleLabel} <span aria-hidden="true">⌄</span>
        </button>
        <button
          className="pdf-properties-trigger"
          title="PDF properties"
          aria-label="PDF properties"
          onClick={onProperties}
        >
          <SlidersHorizontal size={18} />
        </button>
        <button
          ref={moreButton}
          className="pdf-more-trigger"
          title="More PDF actions"
          aria-label="More PDF actions"
          aria-expanded={panel === "more"}
          onClick={() => {
            setContextPoint(null);
            setPanel(panel === "more" ? null : "more");
          }}
        >
          <MoreVertical size={20} />
        </button>
      </div>
      {panel && (
        <div
          className="pdf-options"
          data-context={!!contextPoint}
          style={
            contextPoint
              ? {
                  position: "fixed",
                  left: contextPoint.x,
                  top: contextPoint.y,
                  bottom: "auto",
                  right: "auto",
                }
              : undefined
          }
        >
          {panel === "scale" && (
            <PdfScaleControls
              model={model}
              shape={shape}
              close={() => setPanel(null)}
            />
          )}
          {panel === "pages" && (
            <div className="pdf-page-chooser" aria-label="PDF page thumbnails">
              <div className="pdf-options-heading">
                <strong>Choose page</strong>
                <button
                  type="button"
                  aria-label="Close page chooser"
                  onClick={closePanel}
                >
                  <X size={16} />
                </button>
              </div>
              <small>{name} · Showing up to five nearby pages.</small>
              <form
                className="pdf-page-jump"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (jump !== page) go(jump);
                  else closePanel();
                }}
              >
                <label>
                  Page number
                  <input
                    aria-label="PDF page number"
                    type="number"
                    min={1}
                    max={count}
                    required
                    value={jump}
                    disabled={!!busy || hasAttached}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => setJump(Number(event.target.value))}
                  />
                </label>
                <button type="submit" disabled={!!busy || hasAttached}>
                  Go
                </button>
              </form>
              {hasAttached && (
                <small>
                  This page has attached annotations. Extract a new page to
                  preserve their alignment.
                </small>
              )}
              {thumbnailError && <small role="alert">{thumbnailError}</small>}
              <div className="pdf-page-thumbnails">
                {thumbnails.map((thumbnail) => (
                  <button
                    type="button"
                    key={thumbnail.page}
                    aria-label={`Go to PDF page ${thumbnail.page}`}
                    aria-current={thumbnail.page === page ? "page" : undefined}
                    disabled={
                      !!busy || (thumbnail.page !== page && hasAttached)
                    }
                    onClick={() =>
                      thumbnail.page === page
                        ? setPanel(null)
                        : go(thumbnail.page)
                    }
                  >
                    <img src={thumbnail.url} alt="" />
                    <span>{thumbnail.page}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {panel === "more" && (
            <div className="pdf-action-list" aria-label="PDF actions">
              <button
                onClick={() => {
                  setPanel(null);
                  model.editor.duplicateShapes([shape.id], { x: 24, y: 24 });
                }}
              >
                <Copy size={16} />
                Duplicate
              </button>
              <button
                onClick={() => {
                  setPanel(null);
                  model.editor.deleteShapes([shape.id]);
                }}
              >
                <Trash2 size={16} />
                Delete
              </button>
              <span className="pdf-menu-divider" />
              <button
                disabled={!!busy}
                onClick={() => {
                  setContextPoint(null);
                  setPanel("extract");
                }}
              >
                <Layers size={16} />
                Extract pages
              </button>
              <button
                disabled={!!busy}
                onClick={() => {
                  setContextPoint(null);
                  setPanel("combined");
                }}
              >
                <Layers size={16} />
                Combine pages on one sheet…
              </button>
              <button
                disabled={!!busy}
                onClick={() => {
                  setContextPoint(null);
                  setPanel("sheets");
                }}
              >
                <FilePlus2 size={16} />
                Create sheets from pages…
              </button>
              {/^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(source) && (
                <a href={source} download={name} onClick={() => setPanel(null)}>
                  <Download size={16} />
                  Download
                </a>
              )}
              <span className="pdf-menu-divider" />
              <button
                onClick={() => {
                  setPanel(null);
                  model.editor.bringToFront([shape.id]);
                }}
              >
                <ArrowUpToLine size={16} />
                Bring to front
              </button>
              <button
                onClick={() => {
                  setPanel(null);
                  model.editor.sendToBack([shape.id]);
                }}
              >
                <ArrowDownToLine size={16} />
                Send to back
              </button>
              <button
                onClick={() => {
                  setPanel(null);
                  model.editor.toggleLock([shape.id]);
                  model.editor.select(shape.id);
                }}
              >
                <Lock size={16} />
                Lock
              </button>
              <button
                onClick={() => {
                  setContextPoint(null);
                  setPanel("scale");
                }}
              >
                Scale…
              </button>
              <button
                onClick={() => {
                  setPanel(null);
                  onProperties();
                }}
              >
                <SlidersHorizontal size={16} />
                Properties
              </button>
            </div>
          )}
          {(panel === "extract" ||
            panel === "sheets" ||
            panel === "combined") && (
            <form
              className="pdf-extract-form"
              onSubmit={(event) => {
                event.preventDefault();
                void run(
                  (signal) =>
                    extractPdfPages(
                      model.editor,
                      model.room.id,
                      shape,
                      range,
                      panel === "combined" ? "combined" : panel === "sheets",
                      signal,
                      model.room.platform === "cloudflare",
                      setBusy,
                    ),
                  panel === "sheets" ? "Creating sheets…" : "Extracting pages…",
                );
              }}
            >
              <div className="pdf-options-heading">
                <strong>Arrange PDF pages</strong>
                <button
                  type="button"
                  aria-label="Close PDF options"
                  onClick={closePanel}
                >
                  <X size={16} />
                </button>
              </div>
              <label>
                Place pages as
                <select
                  aria-label="PDF page arrangement"
                  value={panel}
                  disabled={!!busy}
                  onChange={(e) =>
                    setPanel(
                      e.target.value as "extract" | "sheets" | "combined",
                    )
                  }
                >
                  <option value="extract">Separate page objects</option>
                  <option value="sheets">One sheet per page</option>
                  <option value="combined">Together on one sheet</option>
                </select>
              </label>
              <div className="pdf-extract-field">
                <label htmlFor={`pdf-range-${shape.id}`}>Pages</label>
                <input
                  id={`pdf-range-${shape.id}`}
                  aria-label="PDF pages to extract"
                  value={range}
                  placeholder="all or 1, 3–5"
                  disabled={!!busy}
                  onChange={(e) => setRange(e.target.value)}
                />
                <span className="pdf-field-hint">
                  All pages, or a range like 1, 3–5
                </span>
              </div>
              <button
                className="pdf-extract-submit"
                type="submit"
                disabled={!!busy}
              >
                {panel === "sheets"
                  ? "Create sheets"
                  : panel === "combined"
                    ? "Create combined sheet"
                    : "Extract pages"}
              </button>
              <small>Original PDF stays on the board · Max. 50 pages</small>
            </form>
          )}
        </div>
      )}
      {busy && (
        <div className="pdf-operation">
          <span role="status">
            <LoaderCircle className="upload-spinner" size={14} />
            {busy}
          </span>
          <button
            aria-label="Cancel PDF operation"
            onClick={() => job.current?.abort()}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

/** PDF-specific details for the common Properties panel. */
export function PdfProperties({
  model,
  shape,
}: {
  model: WorkspaceModel;
  shape: TLImageShape;
}) {
  const asset =
    shape.props.assetId && model.editor.getAsset(shape.props.assetId);
  const source = asset?.meta.documentSource;
  const name = String(asset?.meta.documentName || shape.props.altText || "PDF");
  const page = Number(asset?.meta.pdfPage) || 1;
  const count = Number(asset?.meta.pdfPages) || 1;
  const [mode, setMode] = useState<"extract" | "sheets" | "combined">(
    "extract",
  );
  const [range, setRange] = useState("all");
  const [busy, setBusy] = useState("");
  const job = useRef<AbortController | null>(null);
  useEffect(() => () => job.current?.abort(), []);
  const disabled = !model.canEdit || shape.isLocked || !!busy;
  const scale = sourceMeters(model.editor, shape);
  return (
    <div className="pdf-properties">
      <section aria-label="PDF source">
        <h4>Source</h4>
        <div className="property-row">
          <span>Document</span>
          <strong title={name}>{name}</strong>
        </div>
        <div className="property-row">
          <span>Page</span>
          <strong>
            {page} of {count}
          </strong>
        </div>
        <div className="property-row">
          <span>Page scale</span>
          <strong>
            {scale > 0
              ? String(shape.meta.calibration || "Set")
              : "Not to scale"}
          </strong>
        </div>
        {typeof source === "string" &&
          /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(source) && (
            <a href={source} download={name}>
              <Download size={15} /> Download original
            </a>
          )}
      </section>
      <section aria-label="PDF page actions">
        <h4>Page actions</h4>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (disabled || job.current) return;
            const controller = new AbortController();
            job.current = controller;
            setBusy(
              mode === "sheets" ? "Creating sheets…" : "Extracting pages…",
            );
            void extractPdfPages(
              model.editor,
              model.room.id,
              shape,
              range,
              mode === "combined" ? "combined" : mode === "sheets",
              controller.signal,
              model.room.platform === "cloudflare",
              setBusy,
            )
              .catch((error) => {
                if (!controller.signal.aborted)
                  notify((error as Error).message);
              })
              .finally(() => {
                job.current = null;
                setBusy("");
              });
          }}
        >
          <label>
            Arrange
            <select
              aria-label="PDF page arrangement"
              value={mode}
              disabled={disabled}
              onChange={(event) => setMode(event.target.value as typeof mode)}
            >
              <option value="extract">Separate page objects</option>
              <option value="sheets">One sheet per page</option>
              <option value="combined">Together on one sheet</option>
            </select>
          </label>
          <label>
            Pages
            <input
              aria-label="PDF pages to extract"
              value={range}
              placeholder="all or 1, 3–5"
              disabled={disabled}
              onChange={(event) => setRange(event.target.value)}
            />
          </label>
          <button type="submit" disabled={disabled}>
            {mode === "sheets"
              ? "Create sheets"
              : mode === "combined"
                ? "Create combined sheet"
                : "Extract pages"}
          </button>
          <small>Original PDF stays on the board · Max. 50 pages</small>
        </form>
        {busy && (
          <div className="pdf-operation" role="status">
            {busy}
            <button type="button" onClick={() => job.current?.abort()}>
              Cancel
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
